import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Observable, Subscription, from, race, timer } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { MfaChallengeBridge } from '../../routes/mfa-challenge/mfa-challenge.component';
import { AuthService } from '../services/auth.service';
import { MicrosoftAuthService } from '../services/microsoft-auth.service';

const ROOT_REDIRECT_TIMEOUT_MS = 1000;
/** How long the guard waits for MSAL to finish processing the redirect hash. */
const MSAL_SETTLE_TIMEOUT_MS = 5000;

/**
 * Root Redirect Guard
 *
 * Handles the root route (`/`) by redirecting authenticated users to the dashboard
 * and unauthenticated users to the login page.
 *
 * SSO-LINK RESUME: when a pending account-setup SSO link is staged
 * (sessionStorage, staged by the account-setup screen before its MSAL
 * redirect), the MSAL redirect lands HERE (the app root) — the guard routes
 * to the setup screen's resume mode instead of /login so the first-time
 * link completes. Authenticated users never have a pending link (the setup
 * screen is guest-only), so the authenticated branch is unchanged.
 *
 * MSAL SETTLE (the two-page-load race, observed live 2026-09-11): the
 * redirect arrives at the root WITH the auth hash (#code=...). MSAL parses
 * that hash asynchronously via handleRedirectObservable. If this guard
 * navigates (SPA) BEFORE that parse completes, the URL loses its hash and
 * MSAL resolves null — the interaction stays in progress, the credential is
 * never captured, and the resume spins forever. So when a pending link
 * exists, the guard FIRST waits for MSAL to settle (inProgress$ → None, or
 * a timeout) and only then routes to the resume screen. By that point the
 * boot hook has captured the ID token, and the resume consumes both.
 */
export const rootRedirectGuard: CanActivateFn = (
  _route,
  _state,
): Observable<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const msalBroadcast = inject(MsalBroadcastService);
  const msalAuth = inject(MicrosoftAuthService);

  return new Observable<boolean | UrlTree>((observer) => {
    // Check if already authenticated (synchronous check)
    if (authService.isAuthenticated) {
      observer.next(router.parseUrl('/dashboard'));
      observer.complete();
      return;
    }

    // A pending SSO link means the MSAL redirect just returned from the
    // account-setup flow. WAIT for MSAL to finish parsing the auth hash
    // before navigating — navigating early strips the hash and breaks the
    // redirect processing (see docblock).
    //
    // MSAL IS LAZY (observed live 2026-09-11): the service defers
    // initialization to the first method call, and handleRedirectObservable
    // only runs inside that initialization. On the redirect landing page
    // nothing calls initialize() (the resume component only subscribes to
    // the result stream), so the hash was NEVER processed — the interaction
    // stayed in progress and the resume spun forever. The guard therefore
    // triggers initialization first; handleRedirectObservable runs inside
    // it, the boot hook captures the credential, and inProgress$ settles.
    if (authService.ssoLinkPending) {
      race(
        // Initialize MSAL (runs handleRedirectObservable inside), then wait
        // for the interaction to settle. Initialization failure falls
        // through to the settle wait anyway — the timeout bounds it.
        from(msalAuth.initialize()).pipe(
          switchMap(() =>
            msalBroadcast.inProgress$.pipe(
              filter((status: InteractionStatus) => status === InteractionStatus.None),
              take(1),
            ),
          ),
        ),
        timer(MSAL_SETTLE_TIMEOUT_MS),
      ).subscribe({
        next: () => {
          observer.next(router.parseUrl('/account-setup/sso-resume'));
          observer.complete();
        },
        error: () => {
          observer.next(router.parseUrl('/account-setup/sso-resume'));
          observer.complete();
        },
      });
      return;
    }

    // Wait for auth state to be determined
    let redirected = false;
    let subscription: Subscription | undefined;

    const decide = (): void => {
      if (redirected) {
        return;
      }
      redirected = true;
      subscription?.unsubscribe();
      if (authService.isAuthenticated) {
        observer.next(router.parseUrl('/dashboard'));
      } else if (authService.ssoLinkPending) {
        observer.next(router.parseUrl('/account-setup/sso-resume'));
      } else if (MfaChallengeBridge.hasPending()) {
        // The SSO exchange staged an MFA challenge while we waited — route
        // straight there instead of bouncing through /login first.
        observer.next(router.parseUrl('/mfa-challenge'));
      } else {
        observer.next(router.parseUrl('/login'));
      }
      observer.complete();
    };

    // MSAL REDIRECT SETTLE (observed live 2026-09-15): a Microsoft redirect
    // lands HERE with the auth hash. AuthService's constructor has already
    // flagged `exchangeInProgress$` true for exactly this window (see
    // `isMsalRedirectHash` there). Deciding off `isAuthenticated$`'s CURRENT
    // (not-yet-resolved) value sent every SSO login through '/login' first —
    // the exchange stages the MFA challenge moments later and LoginComponent
    // bounces a second time to '/mfa-challenge', a visible double-hop. Wait
    // for the exchange to settle (or a timeout) so the MFA case routes
    // straight to /mfa-challenge and never touches /login at all.
    if (authService.isExchangeInProgress) {
      race(
        authService.exchangeInProgress$.pipe(
          filter((inProgress) => !inProgress),
          take(1),
        ),
        timer(MSAL_SETTLE_TIMEOUT_MS),
      ).subscribe(() => decide());
      return;
    }

    subscription = authService.isAuthenticated$.subscribe(() => decide());

    // After timeout, if no auth state determined, redirect to login
    timer(ROOT_REDIRECT_TIMEOUT_MS).subscribe(() => decide());

    return () => subscription?.unsubscribe();
  });
};